/**
 * Passkey Registration Prompt
 *
 * Prompts authenticated users to register a passkey for faster, more secure
 * future logins. Shows after successful authentication if the user hasn't
 * registered a passkey yet.
 *
 * Uses the WebAuthn API through Amplify Auth.
 */

import {
  associateWebAuthnCredential,
  fetchUserAttributes,
  listWebAuthnCredentials,
} from 'aws-amplify/auth';
import { useState, useEffect, type ReactNode } from 'react';

import './passkey-prompt.css';

const PASSKEY_DISMISSED_KEY = 'rmg-passkey-prompt-dismissed';
const PASSKEY_DISMISS_DURATION_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

interface IPasskeyPromptProps {
  /** Called when passkey is successfully registered */
  onSuccess?: () => void;
  /** Called when user dismisses the prompt */
  onDismiss?: () => void;
}

type PromptState = 'idle' | 'registering' | 'success' | 'error';

/**
 * Check if the browser supports WebAuthn passkeys.
 */
function isWebAuthnSupported(): boolean {
  return typeof window !== 'undefined' && typeof window.PublicKeyCredential === 'function';
}

/**
 * Check if user has previously dismissed the passkey prompt.
 */
function wasPromptDismissed(): boolean {
  try {
    const dismissed = localStorage.getItem(PASSKEY_DISMISSED_KEY);
    if (!dismissed) return false;

    const dismissedAt = parseInt(dismissed, 10);
    if (Number.isNaN(dismissedAt)) return false;

    // Check if dismissal has expired
    return Date.now() - dismissedAt < PASSKEY_DISMISS_DURATION_MS;
  } catch {
    return false;
  }
}

/**
 * Mark the prompt as dismissed.
 */
function dismissPrompt(): void {
  try {
    localStorage.setItem(PASSKEY_DISMISSED_KEY, Date.now().toString());
  } catch {
    // Ignore storage errors
  }
}

/**
 * Passkey registration prompt component.
 * Shows a modal prompting the user to register a passkey.
 */
export function PasskeyPrompt({ onSuccess, onDismiss }: IPasskeyPromptProps): ReactNode {
  const [isVisible, setIsVisible] = useState(false);
  const [state, setState] = useState<PromptState>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    // Check conditions for showing the prompt
    const checkShouldShow = async (): Promise<void> => {
      // Don't show if WebAuthn not supported
      if (!isWebAuthnSupported()) {
        return;
      }

      // Don't show if user dismissed recently
      if (wasPromptDismissed()) {
        return;
      }

      try {
        // Verify user is authenticated
        await fetchUserAttributes();

        // Check if user already has passkeys registered
        const { credentials } = await listWebAuthnCredentials();
        if (credentials.length > 0) {
          // User already has passkey(s), don't prompt
          return;
        }

        setIsVisible(true);
      } catch {
        // User not authenticated or error fetching credentials, don't show prompt
      }
    };

    // Small delay to let the main app render first
    const timer = setTimeout(() => {
      void checkShouldShow();
    }, 1500);
    return (): void => {
      clearTimeout(timer);
    };
  }, []);

  const handleRegister = (): void => {
    setState('registering');
    setErrorMessage(null);

    void (async (): Promise<void> => {
      try {
        await associateWebAuthnCredential();
        setState('success');
        onSuccess?.();

        // Auto-close after success
        setTimeout(() => {
          setIsVisible(false);
        }, 2000);
      } catch (error) {
        setState('error');
        const message =
          error instanceof Error ? error.message : 'Failed to register passkey. Please try again.';
        setErrorMessage(message);
      }
    })();
  };

  const handleDismiss = (): void => {
    dismissPrompt();
    setIsVisible(false);
    onDismiss?.();
  };

  const handleMaybeLater = (): void => {
    // Don't persist dismissal, just close for this session
    setIsVisible(false);
    onDismiss?.();
  };

  if (!isVisible) {
    return null;
  }

  return (
    <div className="passkey-overlay" role="dialog" aria-labelledby="passkey-title">
      <div className="passkey-modal">
        <div className="passkey-icon">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M12 2a4 4 0 0 1 4 4v4a4 4 0 0 1-8 0V6a4 4 0 0 1 4-4z" />
            <circle cx="12" cy="14" r="8" />
            <path d="M12 18v-2" />
          </svg>
        </div>

        <h2 id="passkey-title" className="passkey-title">
          Enable Passkey Login
        </h2>

        <p className="passkey-description">
          Sign in faster and more securely with a passkey. Use your fingerprint, face, or device PIN
          instead of remembering a password.
        </p>

        {state === 'error' && errorMessage !== null && (
          <div className="passkey-error" role="alert">
            {errorMessage}
          </div>
        )}

        {state === 'success' && (
          <div className="passkey-success" role="status">
            Passkey registered successfully!
          </div>
        )}

        <div className="passkey-actions">
          {state === 'idle' && (
            <>
              <button
                type="button"
                className="passkey-btn passkey-btn-primary"
                onClick={handleRegister}
              >
                Register Passkey
              </button>
              <button
                type="button"
                className="passkey-btn passkey-btn-secondary"
                onClick={handleMaybeLater}
              >
                Maybe Later
              </button>
              <button
                type="button"
                className="passkey-btn passkey-btn-link"
                onClick={handleDismiss}
              >
                Don&apos;t ask again
              </button>
            </>
          )}

          {state === 'registering' && (
            <div className="passkey-loading">
              <div className="passkey-spinner" />
              <span>Waiting for your device...</span>
            </div>
          )}

          {state === 'error' && (
            <>
              <button
                type="button"
                className="passkey-btn passkey-btn-primary"
                onClick={handleRegister}
              >
                Try Again
              </button>
              <button
                type="button"
                className="passkey-btn passkey-btn-secondary"
                onClick={handleMaybeLater}
              >
                Skip for Now
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
